import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { ArrowRight, Fire, ArrowClockwise, Check, X } from "@phosphor-icons/react";
import {
  HAND_QUIZZES,
  HAND_RANKS,
  POT_ODDS,
  POSTFLOP_SCENARIOS,
  correctAction,
  randomHand,
} from "../simulator/data";
import { logScore } from "../lib/api";

const STORAGE = "sim-scores-v1";

function loadScores() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE) || "{}");
  } catch {
    return {};
  }
}
function saveScores(s) {
  localStorage.setItem(STORAGE, JSON.stringify(s));
}

function Card({ label }) {
  const isRed = label.includes("♥") || label.includes("♦");
  return (
    <div className="card-face aspect-[3/4] w-16 sm:w-20 flex flex-col items-start justify-between p-2 sm:p-3 animate-deal-in">
      <span className={`font-bebas text-3xl sm:text-4xl leading-none ${isRed ? "text-red-600" : "text-black"}`}>{label}</span>
      <span className={`font-bebas text-2xl sm:text-3xl leading-none rotate-180 self-end ${isRed ? "text-red-600" : "text-black"}`}>{label}</span>
    </div>
  );
}

function StatBar({ label, value, color = "text-gold-400" }) {
  return (
    <div className="bg-obsidian-700 border border-white/5 px-4 py-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-bebas text-3xl leading-none mt-1 ${color}`}>{value}</div>
    </div>
  );
}

/* ===== Mode A: Hand ranking + pot odds ===== */
function ModeA({ scores, setScores }) {
  const [subMode, setSubMode] = useState("hand"); // hand | odds
  const [q, setQ] = useState(() => HAND_QUIZZES[Math.floor(Math.random() * HAND_QUIZZES.length)]);
  const [oq, setOq] = useState(() => POT_ODDS[Math.floor(Math.random() * POT_ODDS.length)]);
  const [feedback, setFeedback] = useState(null);
  const [oddsInput, setOddsInput] = useState("");

  const s = scores.A || { correct: 0, total: 0, streak: 0, best: 0 };

  const answer = (key) => {
    if (feedback) return;
    const ok = key === q.answer;
    const streak = ok ? s.streak + 1 : 0;
    const next = {
      correct: s.correct + (ok ? 1 : 0),
      total: s.total + 1,
      streak,
      best: Math.max(s.best, streak),
    };
    const newScores = { ...scores, A: next };
    setScores(newScores);
    saveScores(newScores);
    logScore({ mode: "A", score: next.correct, streak }).catch(() => {});
    setFeedback({ ok, explain: q.explain });
  };

  const nextQ = () => {
    setQ(HAND_QUIZZES[Math.floor(Math.random() * HAND_QUIZZES.length)]);
    setFeedback(null);
  };

  const submitOdds = () => {
    if (feedback) return;
    const parsed = parseInt(oddsInput, 10);
    const ok = !isNaN(parsed) && Math.abs(parsed - oq.answer) <= 2;
    const streak = ok ? s.streak + 1 : 0;
    const next = {
      correct: s.correct + (ok ? 1 : 0),
      total: s.total + 1,
      streak,
      best: Math.max(s.best, streak),
    };
    const newScores = { ...scores, A: next };
    setScores(newScores);
    saveScores(newScores);
    logScore({ mode: "A", score: next.correct, streak }).catch(() => {});
    setFeedback({ ok, explain: `${oq.explain} Your answer: ${parsed || 0}%.` });
  };

  const nextOdds = () => {
    setOq(POT_ODDS[Math.floor(Math.random() * POT_ODDS.length)]);
    setOddsInput("");
    setFeedback(null);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 bg-obsidian-700 border border-white/5 p-6 sm:p-8">
        <div className="flex gap-2 mb-6">
          <button
            data-testid="modeA-hand-tab"
            onClick={() => { setSubMode("hand"); setFeedback(null); }}
            className={`px-4 py-2 font-bebas tracking-wide uppercase text-sm border ${subMode === "hand" ? "border-gold-400 text-gold-400" : "border-white/10 text-zinc-400"}`}
          >
            Hand Ranking
          </button>
          <button
            data-testid="modeA-odds-tab"
            onClick={() => { setSubMode("odds"); setFeedback(null); }}
            className={`px-4 py-2 font-bebas tracking-wide uppercase text-sm border ${subMode === "odds" ? "border-gold-400 text-gold-400" : "border-white/10 text-zinc-400"}`}
          >
            Pot Odds
          </button>
        </div>

        {subMode === "hand" ? (
          <>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
              What's the best hand?
            </div>
            <div className="flex gap-2 sm:gap-3 flex-wrap" data-testid="modeA-cards">
              {q.cards.map((c, i) => <Card key={i} label={c} />)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-8">
              {HAND_RANKS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => answer(r.key)}
                  data-testid={`modeA-answer-${r.key}`}
                  className={`text-left border px-4 py-3 font-bebas uppercase tracking-wide text-sm transition-colors ${
                    feedback && r.key === q.answer
                      ? "border-felt text-felt bg-felt/10"
                      : feedback
                      ? "border-white/10 text-zinc-500"
                      : "border-white/10 text-white hover:border-gold-400 hover:text-gold-400"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
              Compute required equity
            </div>
            <div className="font-mono text-lg text-zinc-200">
              Pot: <span className="text-gold-400">${oq.pot}</span> · You must call: <span className="text-gold-400">${oq.toCall}</span>
            </div>
            <p className="text-zinc-400 text-sm mt-2">
              What equity (%) do you need to break even on this call?
            </p>
            <div className="flex gap-3 mt-6">
              <input
                data-testid="modeA-odds-input"
                type="number"
                value={oddsInput}
                onChange={(e) => setOddsInput(e.target.value)}
                className="bg-obsidian-900 border border-white/10 px-4 py-3 text-white font-mono w-32 focus:outline-none focus:border-gold-400"
                placeholder="%"
              />
              <Button
                onClick={submitOdds}
                data-testid="modeA-odds-submit"
                className="bg-gold-400 text-black hover:brightness-110 rounded-none font-bebas text-lg tracking-wider"
              >
                Lock In
              </Button>
            </div>
          </>
        )}

        {feedback && (
          <div
            data-testid="modeA-feedback"
            className={`mt-6 p-4 border ${feedback.ok ? "border-felt bg-felt/10 text-felt" : "border-scarcity bg-scarcity/10 text-scarcity"}`}
          >
            <div className="flex items-center gap-2 font-bebas text-xl uppercase">
              {feedback.ok ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
              {feedback.ok ? "Correct" : "Not quite"}
            </div>
            <p className="text-sm mt-2 text-zinc-300 font-manrope">{feedback.explain}</p>
            <Button
              onClick={subMode === "hand" ? nextQ : nextOdds}
              className="mt-4 bg-white text-black hover:bg-gold-400 rounded-none font-bebas tracking-wider"
              data-testid="modeA-next"
            >
              Next Hand <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        )}
      </div>

      <div className="lg:col-span-4 space-y-3">
        <StatBar label="Correct" value={s.correct} />
        <StatBar label="Attempts" value={s.total} />
        <StatBar label="Current Streak" value={s.streak} color="text-felt" />
        <StatBar label="Best Streak" value={s.best} color="text-scarcity" />
      </div>
    </div>
  );
}

/* ===== Mode B: preflop range trainer ===== */
function ModeB({ scores, setScores }) {
  const [hand, setHand] = useState(() => randomHand());
  const [feedback, setFeedback] = useState(null);
  const s = scores.B || { correct: 0, total: 0, streak: 0, best: 0 };
  const suitLabel = hand.suited ? "♠" : "♥";
  const suit2 = hand.suited ? "♠" : "♦";
  const correct = useMemo(() => correctAction(hand.position, hand.r1, hand.r2, hand.suited), [hand]);

  const act = (action) => {
    if (feedback) return;
    const ok = action === correct;
    const streak = ok ? s.streak + 1 : 0;
    const next = { correct: s.correct + (ok ? 1 : 0), total: s.total + 1, streak, best: Math.max(s.best, streak) };
    const newScores = { ...scores, B: next };
    setScores(newScores);
    saveScores(newScores);
    logScore({ mode: "B", score: next.correct, streak }).catch(() => {});
    setFeedback({ ok, correct });
  };

  const nextH = () => {
    setHand(randomHand());
    setFeedback(null);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 bg-obsidian-700 border border-white/5 p-6 sm:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Preflop · Open action</div>
          <div className="font-bebas text-2xl tracking-widest">
            POSITION: <span className="text-gold-400">{hand.position}</span>
          </div>
        </div>
        <div className="flex gap-3 mt-6" data-testid="modeB-hand">
          <Card label={hand.r1 + suitLabel} />
          <Card label={hand.r2 + suit2} />
        </div>
        <div className="mt-3 font-mono text-sm text-zinc-500 uppercase tracking-widest">
          {hand.r1}{hand.r2} {hand.r1 !== hand.r2 ? (hand.suited ? "suited" : "offsuit") : ""}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-8">
          {[
            { key: "fold", label: "Fold", color: "border-zinc-500 text-zinc-400 hover:border-white hover:text-white" },
            { key: "call", label: "Call", color: "border-gold-400 text-gold-400 hover:bg-gold-400/10" },
            { key: "raise", label: "Raise", color: "border-scarcity text-scarcity hover:bg-scarcity/10" },
          ].map((a) => (
            <button
              key={a.key}
              onClick={() => act(a.key)}
              data-testid={`modeB-action-${a.key}`}
              className={`border-2 py-4 font-bebas text-xl uppercase tracking-wider transition-colors ${a.color}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {feedback && (
          <div
            data-testid="modeB-feedback"
            className={`mt-6 p-4 border ${feedback.ok ? "border-felt bg-felt/10 text-felt" : "border-scarcity bg-scarcity/10 text-scarcity"}`}
          >
            <div className="flex items-center gap-2 font-bebas text-xl uppercase">
              {feedback.ok ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
              {feedback.ok ? "That's the play" : `Chart says: ${feedback.correct.toUpperCase()}`}
            </div>
            <p className="text-sm mt-2 text-zinc-300 font-manrope">
              From <b>{hand.position}</b>, Lucas's simplified chart says <b>{feedback.correct}</b> with {hand.r1}{hand.r2}{hand.r1 !== hand.r2 ? (hand.suited ? "s" : "o") : ""}.
            </p>
            <Button
              onClick={nextH}
              className="mt-4 bg-white text-black hover:bg-gold-400 rounded-none font-bebas tracking-wider"
              data-testid="modeB-next"
            >
              Next Hand <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        )}
      </div>
      <div className="lg:col-span-4 space-y-3">
        <StatBar label="Correct" value={s.correct} />
        <StatBar label="Attempts" value={s.total} />
        <StatBar label="Current Streak" value={s.streak} color="text-felt" />
        <StatBar label="Best Streak" value={s.best} color="text-scarcity" />
      </div>
    </div>
  );
}

/* ===== Mode C: postflop scenarios ===== */
function ModeC({ scores, setScores }) {
  const [i, setI] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const scenario = POSTFLOP_SCENARIOS[i];
  const s = scores.C || { correct: 0, total: 0, streak: 0, best: 0 };

  const pick = (choice) => {
    if (feedback) return;
    const ok = choice.correct;
    const streak = ok ? s.streak + 1 : 0;
    const next = { correct: s.correct + (ok ? 1 : 0), total: s.total + 1, streak, best: Math.max(s.best, streak) };
    const newScores = { ...scores, C: next };
    setScores(newScores);
    saveScores(newScores);
    logScore({ mode: "C", score: next.correct, streak }).catch(() => {});
    setFeedback({ ok, explain: choice.explain });
  };

  const nextS = () => {
    setI((i + 1) % POSTFLOP_SCENARIOS.length);
    setFeedback(null);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 bg-obsidian-700 border border-white/5 p-6 sm:p-8">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Scenario {i + 1} · {POSTFLOP_SCENARIOS.length}</div>
        <h3 className="font-bebas text-3xl uppercase mt-1">{scenario.title}</h3>

        <div className="mt-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Your hand</div>
          <div className="flex gap-3" data-testid="modeC-hero">
            {scenario.hero.map((c) => <Card key={c} label={c} />)}
          </div>
        </div>
        <div className="mt-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Board</div>
          <div className="flex gap-3 flex-wrap" data-testid="modeC-board">
            {scenario.board.map((c) => <Card key={c} label={c} />)}
          </div>
        </div>

        <p className="text-zinc-300 mt-6 leading-relaxed">{scenario.situation}</p>
        <div className="mt-3 font-mono text-sm text-zinc-500">
          Estimated equity: <span className="text-felt">{scenario.equity}%</span>
        </div>

        <div className="grid gap-3 mt-6">
          {scenario.choices.map((c) => (
            <button
              key={c.key}
              onClick={() => pick(c)}
              data-testid={`modeC-choice-${c.key}`}
              className={`text-left border-2 px-5 py-4 font-bebas text-lg uppercase tracking-wider transition-colors ${
                feedback && c.correct
                  ? "border-felt text-felt bg-felt/10"
                  : feedback
                  ? "border-white/10 text-zinc-500"
                  : "border-white/10 text-white hover:border-gold-400 hover:text-gold-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {feedback && (
          <div
            data-testid="modeC-feedback"
            className={`mt-6 p-4 border ${feedback.ok ? "border-felt bg-felt/10 text-felt" : "border-scarcity bg-scarcity/10 text-scarcity"}`}
          >
            <div className="flex items-center gap-2 font-bebas text-xl uppercase">
              {feedback.ok ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
              {feedback.ok ? "Sharp" : "Reconsider"}
            </div>
            <p className="text-sm mt-2 text-zinc-300 font-manrope">{feedback.explain}</p>
            <Button
              onClick={nextS}
              className="mt-4 bg-white text-black hover:bg-gold-400 rounded-none font-bebas tracking-wider"
              data-testid="modeC-next"
            >
              Next Scenario <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        )}
      </div>
      <div className="lg:col-span-4 space-y-3">
        <StatBar label="Correct" value={s.correct} />
        <StatBar label="Attempts" value={s.total} />
        <StatBar label="Current Streak" value={s.streak} color="text-felt" />
        <StatBar label="Best Streak" value={s.best} color="text-scarcity" />
      </div>
    </div>
  );
}

export default function Simulator() {
  const [scores, setScores] = useState(loadScores);

  const reset = () => {
    localStorage.removeItem(STORAGE);
    setScores({});
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 grain">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold-400 flex items-center gap-2">
            <Fire size={14} weight="fill" /> HAND SIMULATOR · FREE
          </div>
          <h1 className="font-bebas text-5xl sm:text-6xl uppercase mt-2">Test your instincts.</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Three modes. Track your streak. No account, no ads. Local-only.
          </p>
        </div>
        <Button
          onClick={reset}
          variant="outline"
          data-testid="sim-reset-btn"
          className="border-white/10 bg-transparent text-zinc-400 hover:text-white hover:bg-white/5 rounded-none font-bebas tracking-wider"
        >
          <ArrowClockwise size={16} className="mr-2" /> Reset Scores
        </Button>
      </div>

      <Tabs defaultValue="A" className="w-full" data-testid="simulator-tabs">
        <TabsList className="bg-obsidian-700 border border-white/5 rounded-none p-1 mb-6 h-auto">
          <TabsTrigger value="A" data-testid="tab-A" className="font-bebas tracking-wider uppercase text-base px-6 py-2 rounded-none data-[state=active]:bg-gold-400 data-[state=active]:text-black">
            A · Hand Ranking + Odds
          </TabsTrigger>
          <TabsTrigger value="B" data-testid="tab-B" className="font-bebas tracking-wider uppercase text-base px-6 py-2 rounded-none data-[state=active]:bg-gold-400 data-[state=active]:text-black">
            B · Preflop Range
          </TabsTrigger>
          <TabsTrigger value="C" data-testid="tab-C" className="font-bebas tracking-wider uppercase text-base px-6 py-2 rounded-none data-[state=active]:bg-gold-400 data-[state=active]:text-black">
            C · Postflop
          </TabsTrigger>
        </TabsList>
        <TabsContent value="A"><ModeA scores={scores} setScores={setScores} /></TabsContent>
        <TabsContent value="B"><ModeB scores={scores} setScores={setScores} /></TabsContent>
        <TabsContent value="C"><ModeC scores={scores} setScores={setScores} /></TabsContent>
      </Tabs>

      <div className="mt-20 bg-gradient-to-r from-gold-400/10 to-transparent border-l-4 border-gold-400 p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h3 className="font-bebas text-3xl uppercase">Ready for the real thing?</h3>
          <p className="text-zinc-400 mt-2">Book a Boot Camp seat and drill this stuff live with Lucas.</p>
        </div>
        <Link to="/#bootcamp" data-testid="sim-cta-bootcamp">
          <Button className="bg-gradient-to-r from-gold-400 to-gold-500 text-black hover:brightness-110 rounded-none font-bebas text-lg tracking-wider px-6 py-6">
            Reserve a Seat <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </div>
    </div>
  );
}
